import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import 'dayjs/locale/pt-br.js';
import { useAuth } from '../contexts/AuthContext.js';
import { entityService } from '../services/entityService.js';
import { reviewService } from '../services/reviewService.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import LoadingModal from './LoadingModal.js';
import './Reviews.css';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

function Reviews({ onLogout }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados da entidade
      const entityResult = await entityService.getMyEntities();
      if (entityResult.success && entityResult?.entities?.length > 0) {
        const entityData = entityResult.entities[0];
        setEntity(entityData);
        
        // Buscar reviews da entidade
        const reviewsResult = await reviewService.getEntityReviews(entityData.id);
        if (reviewsResult.success) {
          setReviews(reviewsResult.reviews);
        }
      } else {
        setError('Nenhuma entidade encontrada');
      }
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (score) => {
    return [...Array(5)].map((_, i) => (
      <svg 
        key={i} 
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill={i < score ? '#F59E0B' : 'none'} 
        stroke="#F59E0B" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ));
  };

  if (loading) {
    return (
      <div className="reviews-container">
        <LoadingModal isOpen={true} message="Carregando avaliações..." />
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="reviews-container">
        <header className="reviews-header">
          <div className="header-left">
            <Logo className="dashboard-logo" />
            <p className="panel-subtitle">Avaliações</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/dashboard')} className="back-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Voltar
            </button>
          </div>
        </header>
        <div className="error-state">
          <p>{error || 'Erro ao carregar dados'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="reviews-container">
      <LoadingModal isOpen={!!loadingMessage} message={loadingMessage} />
      
      {/* Header */}
      <header className="reviews-header">
        <div className="header-left">
          <Logo className="dashboard-logo" />
          <div>
            <h1 className="page-title">Avaliações</h1>
            <p className="page-subtitle">{entity?.name}</p>
          </div>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar ao Dashboard
          </button>
        </div>
      </header>

      <main className="reviews-main">
        {/* Overview Card */}
        <div className="reviews-overview">
          <div className="overview-card">
            <div className="overview-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <div className="overview-content">
              <h2 className="overview-title">Avaliação Geral</h2>
              <div className="overview-score">
                <span className="score-value">{entity?.ratingScore?.toFixed(1) || '0.0'}</span>
                <div className="score-stars">
                  {renderStars(Math.round(entity?.ratingScore || 0))}
                </div>
              </div>
              <p className="overview-total">
                Baseado em {entity?.ratingCount || 0} {entity?.ratingCount === 1 ? 'avaliação' : 'avaliações'}
              </p>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="reviews-content">
          <div className="reviews-list-header">
            <h3 className="section-title">Todas as Avaliações</h3>
            <span className="reviews-count">{reviews.length} {reviews.length === 1 ? 'avaliação' : 'avaliações'}</span>
          </div>

          {reviews.length === 0 ? (
            <div className="empty-reviews">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              <h3>Ainda não há avaliações</h3>
              <p>As avaliações dos seus clientes aparecerão aqui</p>
            </div>
          ) : (
            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <img 
                      src={review.user?.image || 'https://via.placeholder.com/48'} 
                      alt={review.user?.name}
                      className="review-user-avatar"
                    />
                    <div className="review-user-info">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h4 className="review-user-name">{review.user?.name}</h4>
                        {review.schedule?.dateTime && (
                          <span style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                            Atendimento realizado em {dayjs(review.schedule.dateTime).format('MMMM [de] YYYY')}
                          </span>
                        )}
                      </div>
                      <span className="review-date">{dayjs(review.createdAt).fromNow()}</span>
                    </div>
                    {/* <div className="review-rating">
                      {renderStars(review.score)}
                      <span className="rating-value">{review.score}</span>
                    </div> */}
                  </div>
                  
                  {review.content && (
                    <div className="review-body">
                      <p className="review-text">{review.content}</p>
                    </div>
                  )}
                  
                  <div className="review-footer">
                    <span className={`review-status ${review.active ? 'active' : 'inactive'}`}>
                      {review.active ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Reviews;
